import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Briefcase, ChevronRight } from 'lucide-react';
import { useTheme } from '../ThemeProvider';
import { businessTokens } from './businessTheme';
import { EVENTS } from '../../constants/navigation';
import { BusinessService } from '../../services/BusinessService';
import type { BusinessProfile } from '../../types/business';

/**
 * Links from a personal profile to the businesses that person runs.
 *
 * Only profiles that are active AND have showOnPersonalProfile on. That toggle
 * is per business rather than account-wide, so someone running three of them can
 * put one in front of their friends and keep the other two to themselves —
 * which is the whole reason the field sits on business_profiles.
 *
 * Renders nothing when there is nothing to link, so a profile with no business
 * gains no empty row.
 */
export function BusinessProfileBadges({ ownerId }: { ownerId: string }) {
  const { theme } = useTheme();
  const t = businessTokens(theme);
  const [profiles, setProfiles] = useState<BusinessProfile[]>([]);

  useEffect(() => {
    let active = true;
    if (!ownerId) return;
    (async () => {
      const linked = await BusinessService.getLinkedProfiles(ownerId);
      if (active) setProfiles(linked);
    })();
    return () => { active = false; };
  }, [ownerId]);

  if (profiles.length === 0) return null;

  return (
    <div style={{ padding: '0 24px', marginTop: 12, display: 'grid', gap: 8 }}>
      {profiles.map(profile => (
        <motion.button
          key={profile.id}
          type="button"
          whileTap={{ scale: 0.98 }}
          onClick={() => window.dispatchEvent(
            new CustomEvent(EVENTS.NAVIGATE_TO_BUSINESS_PROFILE, { detail: { id: profile.id } }),
          )}
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 14, cursor: 'pointer',
            background: t.surface, border: `1px solid ${t.border}`,
            display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
          }}
        >
          {profile.portfolioImages?.[0]
            ? (
              <img
                src={profile.portfolioImages[0]}
                alt=""
                loading="lazy"
                style={{ width: 32, height: 32, borderRadius: 9, objectFit: 'cover', flexShrink: 0 }}
              />
            )
            : (
              <div
                style={{
                  width: 32, height: 32, borderRadius: 9, flexShrink: 0, background: t.accentSoft,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Briefcase style={{ width: 15, height: 15, color: t.accent }} />
              </div>
            )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: t.textMuted }}>
              BUSINESS
            </p>
            <p
              style={{
                margin: 0, fontSize: 14, fontWeight: 700, color: t.text,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {profile.businessName}
            </p>
          </div>
          <ChevronRight style={{ width: 16, height: 16, color: t.textMuted, flexShrink: 0 }} />
        </motion.button>
      ))}
    </div>
  );
}
